import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { invoiceService } from '../../services/invoiceService';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';

import SubscriptionCard from './components/SubscriptionCard';
import PaymentHistoryTable from './components/PaymentHistoryTable';
import InvoicesList from './components/InvoicesList';

const MemberBillingPortal = () => {
  const navigate = useNavigate();
  const { user, currentTenant, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('subscription');
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadBillingData();
    }
  }, [user, currentTenant]);

  const loadBillingData = async () => {
    setLoading(true);
    setError('');
    try {
      // Load active subscription
      const { data: subscriptionData, error: subError } = await supabase
        ?.from('member_subscriptions')
        ?.select(`
          *,
          membership_plans:plan_id(id, plan_name, plan_code, price, billing_cycle, features)
        `)
        ?.eq('member_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('status', 'active')
        ?.maybeSingle();

      if (subError) throw subError;
      setSubscription(subscriptionData);

      // Load payment history
      const { data: paymentsData, error: paymentsError } = await supabase
        ?.from('billing_records')
        ?.select('*')
        ?.eq('member_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('billing_date', { ascending: false })
        ?.limit(50);

      if (paymentsError) throw paymentsError;
      setPaymentHistory(paymentsData || []);

      // Load invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        ?.from('billing_records')
        ?.select('*')
        ?.eq('member_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.not('invoice_number', 'is', null)
        ?.order('billing_date', { ascending: false });

      if (invoicesError) throw invoicesError;
      setInvoices(invoicesData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load billing data');
      console.error('Load billing data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    setDownloadingInvoice(invoiceId);
    setError('');
    try {
      const result = await invoiceService?.downloadInvoice(invoiceId, currentTenant?.id);
      if (result?.success) {
        setSuccess('Invoice downloaded successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(result?.error);
      }
    } catch (err) {
      setError(err?.message || 'Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) return;

    try {
      const { error: cancelError } = await supabase
        ?.from('member_subscriptions')
        ?.update({
          status: 'cancelled',
          cancellation_date: new Date()?.toISOString(),
          cancellation_reason: 'Member requested cancellation'
        })
        ?.eq('id', subscription?.id);

      if (cancelError) throw cancelError;
      setSuccess('Subscription cancelled successfully');
      loadBillingData();
    } catch (err) {
      setError(err?.message || 'Failed to cancel subscription');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading billing information...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  const tabs = [
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard' },
    { id: 'payments', label: 'Payment History', icon: 'Receipt' },
    { id: 'invoices', label: 'Invoices', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Billing Portal</h1>
          <p className="text-muted-foreground">Manage your subscription, view payment history, and access invoices</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <Icon name="X" size={20} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">Success</p>
              <p className="text-green-600 text-sm">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600">
              <Icon name="X" size={20} />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-border">
          <div className="flex gap-1">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 border-b-2 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'subscription' && (
            <SubscriptionCard
              subscription={subscription}
              onCancel={handleCancelSubscription}
              onReload={loadBillingData}
            />
          )}

          {activeTab === 'payments' && (
            <PaymentHistoryTable
              payments={paymentHistory}
              loading={loading}
            />
          )}

          {activeTab === 'invoices' && (
            <InvoicesList
              invoices={invoices}
              onDownload={handleDownloadInvoice}
              downloadingId={downloadingInvoice}
              loading={loading}
            />
          )}
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default MemberBillingPortal;